import React from "react";
import VerificationBadge from "./VerificationBadge.jsx";

export default function TestCaseTable({
  testCases, patientMap, lastRunMap, selectedIds, activeCalls,
  onToggleSelect, onRun, onRunLive, onEdit, onDelete, onViewRun,
}) {
  if (testCases.length === 0) {
    return (
      <div style={s.empty}>
        <p style={s.emptyText}>No test cases yet</p>
        <p style={s.emptySub}>Create a test case to get started.</p>
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>
              <input
                type="checkbox"
                style={s.checkbox}
                checked={testCases.length > 0 && selectedIds.size === testCases.length}
                onChange={() => {
                  if (selectedIds.size === testCases.length) {
                    // Deselect all — call onToggleSelect for each to clear
                    for (const tc of testCases) {
                      if (selectedIds.has(tc.id)) onToggleSelect(tc.id);
                    }
                  } else {
                    // Select all
                    for (const tc of testCases) {
                      if (!selectedIds.has(tc.id)) onToggleSelect(tc.id);
                    }
                  }
                }}
              />
            </th>
            <th style={s.th}>Name</th>
            <th style={s.th}>Patient</th>
            <th style={s.th}>Tags</th>
            <th style={s.th}>Last Result</th>
            <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {testCases.map((tc) => {
            const lastRun = lastRunMap[tc.id];
            const isRunning = activeCalls.includes(tc.id);
            // Three cases for the patient binding on a test case:
            //   1. persona set → new-patient referral, show persona name
            //   2. patientId set + patient found in map → existing patient
            //   3. fallback (orphaned) → show raw patientId or a placeholder
            const hasPersona = tc.persona && tc.persona.firstName && tc.persona.lastName;
            const patient = tc.patientId ? patientMap[tc.patientId] : null;
            let patientName;
            let patientSublabel;
            if (hasPersona) {
              patientName = `${tc.persona.firstName} ${tc.persona.lastName}`;
              patientSublabel = "new patient";
            } else if (patient) {
              patientName = `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
              patientSublabel = tc.patientId;
            } else {
              patientName = tc.patientId || "—";
              patientSublabel = tc.patientId ? "(not found)" : "";
            }

            return (
              <tr key={tc.id} style={s.tr}>
                <td style={s.td}>
                  <input
                    type="checkbox"
                    style={s.checkbox}
                    checked={selectedIds.has(tc.id)}
                    onChange={() => onToggleSelect(tc.id)}
                  />
                </td>
                <td style={s.td}>
                  <div style={s.nameCell}>
                    <span style={s.name}>{tc.name}</span>
                    <span style={s.desc}>{tc.taskDescription?.slice(0, 70)}...</span>
                  </div>
                </td>
                <td style={s.td}>
                  <div style={s.patientCell}>
                    <span style={s.patientName}>{patientName}</span>
                    <span style={hasPersona ? s.patientPersonaTag : s.patientId}>
                      {patientSublabel}
                    </span>
                  </div>
                </td>
                <td style={s.td}>
                  <div style={s.tagWrap}>
                    {(tc.tags || []).slice(0, 3).map((tag) => (
                      <span key={tag} style={s.tag}>{tag}</span>
                    ))}
                  </div>
                </td>
                <td style={s.td}>
                  {isRunning ? (
                    <span style={s.running}>
                      <span style={s.dot} />Running
                    </span>
                  ) : lastRun ? (
                    <span onClick={() => onViewRun(lastRun.id)} style={{ cursor: "pointer" }}>
                      <VerificationBadge verification={lastRun.verification} />
                    </span>
                  ) : (
                    <span style={s.noRun}>&mdash;</span>
                  )}
                </td>
                <td style={{ ...s.td, textAlign: "right" }}>
                  <div style={s.actions}>
                    {isRunning ? (
                      <span style={s.spinner} />
                    ) : (
                      <>
                        <button
                          style={s.actionBtnDraft}
                          onClick={() => onRun(tc.id)}
                          title="Run Draft"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          <span style={s.actionLabel}>Draft</span>
                        </button>
                        <button
                          style={s.actionBtnLive}
                          onClick={() => onRunLive && onRunLive(tc.id)}
                          title="Run Live"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          <span style={s.actionLabel}>Live</span>
                        </button>
                      </>
                    )}
                    <button style={s.actionBtn} onClick={() => onEdit(tc.id)} title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button style={s.actionBtnDanger} onClick={() => onDelete(tc.id)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  wrap: {
    margin: "0 32px 32px",
    border: "1px solid #e5e5e5",
    borderRadius: 10,
    overflow: "hidden",
    marginTop: 12,
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "10px 16px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 500,
    color: "#666",
    borderBottom: "1px solid #e5e5e5",
    background: "#fafafa",
    letterSpacing: "0.01em",
  },
  tr: {
    borderBottom: "1px solid #f0f0f0",
    transition: "background 0.1s",
  },
  td: {
    padding: "12px 16px",
    fontSize: 14,
    verticalAlign: "middle",
  },
  checkbox: {
    width: 15, height: 15, cursor: "pointer", accentColor: "#0f0f0f",
  },
  nameCell: { display: "flex", flexDirection: "column", gap: 2 },
  name: { fontWeight: 500, color: "#0f0f0f", fontSize: 14 },
  desc: { fontSize: 12, color: "#999", lineHeight: 1.3 },
  patientCell: { display: "flex", flexDirection: "column", gap: 1 },
  patientName: { fontWeight: 500, color: "#0f0f0f", fontSize: 13 },
  patientId: { fontSize: 11, color: "#999", fontFamily: "monospace" },
  patientPersonaTag: {
    fontSize: 10, color: "#1d4ed8", background: "#eef4ff",
    padding: "1px 6px", borderRadius: 6, fontWeight: 500, alignSelf: "flex-start",
    marginTop: 2,
  },
  tagWrap: { display: "flex", gap: 4, flexWrap: "wrap" },
  tag: {
    padding: "2px 8px",
    borderRadius: 10,
    background: "#f0f0f0",
    color: "#555",
    fontSize: 11,
    fontWeight: 500,
  },
  running: {
    display: "inline-flex", alignItems: "center", gap: 6,
    color: "#f59e0b", fontSize: 13, fontWeight: 500,
  },
  dot: {
    width: 6, height: 6, borderRadius: "50%", background: "#f59e0b",
    display: "inline-block",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  noRun: { color: "#d1d5db", fontSize: 14 },
  actions: { display: "flex", gap: 3, justifyContent: "flex-end", alignItems: "center" },
  actionBtnDraft: {
    display: "inline-flex", alignItems: "center", gap: 3,
    padding: "3px 8px", border: "1px solid #bfdbfe", borderRadius: 5,
    background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 500,
    cursor: "pointer",
  },
  actionBtnLive: {
    display: "inline-flex", alignItems: "center", gap: 3,
    padding: "3px 8px", border: "1px solid #fecaca", borderRadius: 5,
    background: "#fef2f2", color: "#dc2626", fontSize: 11, fontWeight: 500,
    cursor: "pointer",
  },
  actionLabel: { fontSize: 10, fontWeight: 600, letterSpacing: "0.02em" },
  actionBtn: {
    width: 30, height: 30, border: "none", borderRadius: 6,
    background: "transparent", color: "#666", display: "flex",
    alignItems: "center", justifyContent: "center", transition: "all 0.15s",
  },
  actionBtnDanger: {
    width: 30, height: 30, border: "none", borderRadius: 6,
    background: "transparent", color: "#999", display: "flex",
    alignItems: "center", justifyContent: "center", transition: "all 0.15s",
  },
  spinner: {
    width: 12, height: 12, border: "2px solid #e5e5e5", borderTopColor: "#f59e0b",
    borderRadius: "50%", display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },
  empty: {
    margin: "0 32px", border: "1px solid #e5e5e5", borderRadius: 10,
    padding: "48px 20px", textAlign: "center", marginTop: 12,
  },
  emptyText: { fontSize: 15, fontWeight: 500, color: "#333" },
  emptySub: { fontSize: 13, color: "#999", marginTop: 4 },
};
